import { useCallback, useEffect, useState } from 'react';

import { subDays } from 'date-fns';

import { localStorageKeys } from '../../../localStorageKeys';

type WhiteboardStorageState = {
    classId: string;
    createdOn: Date;
    updatedOn: Date;
    pageIdsBySessionIds: Record<string, string>;
};

/**
 * Custom hook for managing whiteboard persistent state with concurrent session safety.
 * Specifically used for tracking page IDs for different whiteboard sessionIds.
 */
export function useWhiteboardPersistentState(classId: string | undefined, sessionId: string | number | undefined) {
    const [state, setState] = useState<WhiteboardStorageState | undefined>(undefined);

    /**
     * Safely read the current state from localStorage with error handling
     */
    const readCurrentState = useCallback((): WhiteboardStorageState | undefined => {
        try {
            const item = window.localStorage.getItem(localStorageKeys.whiteboardState);
            if (!item || item === 'undefined') {
                return undefined;
            }
            const parsed = JSON.parse(item) as WhiteboardStorageState;
            // Ensure dates are properly parsed
            return {
                ...parsed,
                createdOn: new Date(parsed.createdOn),
                updatedOn: new Date(parsed.updatedOn)
            };
        } catch (error) {
            console.warn(`Error reading localStorage key "${localStorageKeys.whiteboardState}":`, error);
            return undefined;
        }
    }, []);

    /**
     * Safely write state to localStorage with error handling
     */
    const writeState = useCallback((newState: WhiteboardStorageState, currentReactState?: WhiteboardStorageState) => {
        try {
            // Read current localStorage state to check if data actually changed
            const currentStorageState = (() => {
                try {
                    const item = window.localStorage.getItem(localStorageKeys.whiteboardState);
                    if (!item || item === 'undefined') return undefined;
                    return JSON.parse(item) as WhiteboardStorageState;
                } catch {
                    return undefined;
                }
            })();

            // Check if meaningful data has changed (ignore timestamps)
            const dataChanged =
                !currentStorageState ||
                currentStorageState.classId !== newState.classId ||
                JSON.stringify(currentStorageState.pageIdsBySessionIds) !==
                    JSON.stringify(newState.pageIdsBySessionIds);

            // Only update timestamp if data actually changed
            const stateToSave = {
                ...newState,
                updatedOn: dataChanged ? new Date() : currentStorageState?.updatedOn || new Date()
            };

            window.localStorage.setItem(localStorageKeys.whiteboardState, JSON.stringify(stateToSave));

            // Only update React state if the meaningful data has changed (ignore updatedOn)
            // If no currentReactState is provided, always update (e.g., during initialization)
            if (
                !currentReactState ||
                currentReactState.classId !== newState.classId ||
                JSON.stringify(currentReactState.pageIdsBySessionIds) !== JSON.stringify(newState.pageIdsBySessionIds)
            ) {
                setState(stateToSave);
            }
        } catch (error) {
            console.warn(`Error setting localStorage key "${localStorageKeys.whiteboardState}":`, error);
        }
    }, []);

    /**
     * Check if stored data is stale (older than 1 day based on updatedOn)
     */
    const isDataStale = useCallback((storedState: WhiteboardStorageState): boolean => {
        const updatedOn = new Date(storedState.updatedOn);
        return updatedOn < subDays(new Date(), 1);
    }, []);

    /**
     * Create a fresh state for the current class
     */
    const createFreshState = useCallback((currentClassId: string): WhiteboardStorageState => {
        const now = new Date();
        return {
            classId: currentClassId,
            createdOn: now,
            updatedOn: now,
            pageIdsBySessionIds: {}
        };
    }, []);

    /**
     * Initialize or reset state when classId changes or data is stale
     */
    const initializeState = useCallback(() => {
        if (!classId) {
            setState(undefined);
            return;
        }

        const currentState = readCurrentState();

        // If no stored state, class changed, or data is stale, create fresh state
        if (!currentState || currentState.classId !== classId || isDataStale(currentState)) {
            const freshState = createFreshState(classId);
            // Don't pass currentReactState during initialization since we're creating fresh state
            writeState(freshState);
            return;
        }

        setState(currentState);
    }, [classId, readCurrentState, isDataStale, createFreshState, writeState]);

    /**
     * Safely update the page ID for a specific session with concurrent access protection
     */
    const updateSessionPageId = useCallback(
        (targetSessionId: string, pageId: string) => {
            if (!classId) return;

            // Always read the latest state from localStorage before making changes
            const currentState = readCurrentState();
            const pageIdsBySessionIds = currentState?.pageIdsBySessionIds || {};

            // If no state exists or class changed, initialize fresh state
            if (!currentState || currentState.classId !== classId) {
                const freshState = createFreshState(classId);
                freshState.pageIdsBySessionIds[targetSessionId] = pageId;
                writeState(freshState, state);
                return;
            }

            // If data is stale, reset and start fresh
            if (isDataStale(currentState)) {
                const freshState = createFreshState(classId);
                freshState.pageIdsBySessionIds[targetSessionId] = pageId;
                writeState(freshState, state);
                return;
            }

            // Check if the page ID is already set to the same value for this specific session
            const currentPageId = pageIdsBySessionIds?.[targetSessionId];
            if (currentPageId === pageId) {
                // Also check if this pageId exists in other sessions - if so, we should still clean it up
                const otherSessionsWithPageId = pageIdsBySessionIds
                    ? Object.keys(pageIdsBySessionIds).filter(
                          (sessionKey) => pageIdsBySessionIds[sessionKey] === pageId && sessionKey !== targetSessionId
                      )
                    : [];

                if (otherSessionsWithPageId.length === 0) {
                    return; // No need to update if it's the same value and no cleanup needed
                }
            }

            // Remove the pageId from any other sessions (since a pageId can only exist for one session)
            const cleanedpageIdsBySessionIds = Object.fromEntries(
                Object.entries(pageIdsBySessionIds).filter(
                    ([sessionKey, id]) => id !== pageId || sessionKey === targetSessionId
                )
            );

            // Update only the specific session's page ID (updatedOn will be set by writeState)
            const updatedState: WhiteboardStorageState = {
                ...currentState,
                pageIdsBySessionIds: {
                    ...cleanedpageIdsBySessionIds,
                    [targetSessionId]: pageId
                }
            };

            writeState(updatedState, state);
        },
        [classId, readCurrentState, isDataStale, createFreshState, writeState, state]
    );

    /**
     * Get the page ID for the current session
     */
    const getCurrentSessionPageId = useCallback(
        (): string | undefined => state?.pageIdsBySessionIds?.[String(sessionId)],
        [state, sessionId]
    );

    /**
     * Clear all stored state (useful for logout/cleanup)
     */
    const clearState = useCallback(() => {
        try {
            window.localStorage.removeItem(localStorageKeys.whiteboardState);
            setState(undefined);
        } catch (error) {
            console.warn(`Error clearing localStorage key "${localStorageKeys.whiteboardState}":`, error);
        }
    }, []);

    // Initialize state when classId changes
    useEffect(() => {
        initializeState();
    }, [initializeState]);

    return {
        state,
        updateSessionPageId,
        getCurrentSessionPageId,
        clearState,
        isDataStale: state ? isDataStale(state) : false
    };
}
